// A rehearsal of the two draw realms, in memory, for while they are not
// deployed. Nothing about it is hidden from the player: every page that runs it
// says REHEARSAL across the top and refuses to quote a live number.
//
// It answers exactly the interface src/draw.js describes, so the page's real
// compositions (readLive, readRecent, readDrawRow) run over it unchanged and
// the live path and this one cannot drift apart. The rules are the realms':
//
//   Buy         needs an allowance for the gross cost; refused once the draw is
//               committed; the fee is ESCROWED in the draw, not banked (finding
//               25), so spendOf records the GROSS and a refund returns all of it
//   Commit      only a draw that is OVER, and only one that sold something; it
//               freezes the total, which is what stops a buyer waiting for the
//               randomness and then buying the winning entry
//   Resolve     needs the pinned round published; winner = the holder of entry
//               randomnessMod(randomness, frozenTotal); the fee is earned HERE
//               and nowhere else
//   Refund      only past the grace window, and it ABANDONS the draw for good
//
// The clock runs fast on purpose: a rehearsal week that takes a week is not a
// rehearsal. `periodBlocks` is small and the beacon ticks every few seconds.
import { CONFIG } from '../config.js';
import { drawWindow, normaliseDraw } from '../draw.js';

const ME = 'g1you000000000000000000000000000rehearsal';
const OTHERS = [
  'g1m4rt1n9k2xq7v8w3n5r6t0y1u2i3o4p5a6s7dfg',
  'g1sa11yq2w3e4r5t6y7u8i9o0p1a2s3d4f5g6h7jk',
  'g1p3t3r4t5y6u7i8o9p0a1s2d3f4g5h6j7k8l9zxc',
];

const randHex = () => Array.from(crypto.getRandomValues(new Uint8Array(32)), (b) => b.toString(16).padStart(2, '0')).join('');

// speed.gno's randomnessMod, digit by digit over the hex string, so the
// rehearsal picks its winner the way the realm will.
export function randomnessMod(randomness, m) {
  const mod = BigInt(m);
  if (mod <= 0n) throw new Error('rehearsal: randomness modulus must be positive');
  if (!randomness) throw new Error('rehearsal: empty randomness');
  let r = 0n;
  for (const ch of randomness) {
    const d = BigInt(parseInt(ch, 16));
    if (Number.isNaN(Number(d))) throw new Error('rehearsal: randomness is not hex');
    r = (r * 16n + d) % mod;
  }
  return r;
}

export function makeDrawMock({ game = 'speed', cadenceMs = 4000 } = {}) {
  const raffle = game === 'raffle';
  const d = CONFIG.draw;
  // Short enough to watch a whole cycle, long enough that the countdown means
  // something: ~36 blocks a period at the rehearsal's 2 blocks a tick.
  const periodBlocks = 36n;
  const ticketPrice = raffle ? d.raffleTicketPrice : d.speedTicketPrice;
  const entryUnitPrice = raffle ? d.entryUnitPrice : null;
  const unitsPerTicket = raffle ? ticketPrice / entryUnitPrice : 1n;
  const graceBlocks = periodBlocks * 3n;

  const st = {
    launchHeight: 1_000n,
    // An EVEN offset into the period, because the rehearsal's clock advances two
    // blocks a tick and periodBlocks is even: that makes every boundary height
    // land exactly on a tick, so the rehearsal really does pass through the
    // moment the realms' doc comments are about (the first height of the next
    // period, where the index has already moved on) instead of stepping over it.
    height: 1_000n + periodBlocks * 4n + 12n,
    latest: 5_402_000n,
    rounds: new Map(),
    draws: new Map(),        // index -> draw record
    balance: 900_000_000n,
    allowance: 0n,
    opsPot: 0n,
    heldCuts: 0n,
  };
  st.rounds.set(st.latest, randHex());

  const current = () => (st.height - st.launchHeight) / periodBlocks;
  // The round a draw is pinned to at record creation — never chosen later, which
  // is the security property both realms spell out at getOrCreateDraw.
  const pinFor = (index) => st.latest + 2n + (BigInt(index) - current()) * 3n;

  const blank = (index) => ({
    index: BigInt(index),
    committed: false, frozenTotal: 0n, round: pinFor(index), pot: 0n,
    resolved: false, winner: '', abandoned: false, sponsored: 0n, committedAt: 0n,
    cut: 0n, claimPerUnit: 0n, claims: 0n,
    appends: [],                      // [{ addr, units }] — fenwick's ranges
    ticketsOf: new Map(), spendOf: new Map(), earnedOf: new Map(), claimed: new Set(),
  });
  const get = (index) => st.draws.get(String(BigInt(index))) ?? null;
  const getOrCreate = (index) => {
    const k = String(BigInt(index));
    if (!st.draws.has(k)) st.draws.set(k, blank(index));
    return st.draws.get(k);
  };
  const totalOf = (rec) => rec.appends.reduce((s, a) => s + a.units, 0n);
  const ownerOfUnit = (rec, k) => {
    let acc = 0n;
    for (const a of rec.appends) { acc += a.units; if (k < acc) return a.addr; }
    throw new Error('rehearsal: entry out of range');
  };
  const append = (rec, addr, units, earned = false) => {
    rec.appends.push({ addr, units });
    rec.ticketsOf.set(addr, (rec.ticketsOf.get(addr) ?? 0n) + units);
    if (earned) rec.earnedOf.set(addr, (rec.earnedOf.get(addr) ?? 0n) + units);
  };
  const cutOf = (cost) => (cost * CONFIG.draw.houseCutBps) / 10_000n;

  // A purchase by somebody else, for a draw in the past or the present.
  const seedBuy = (index, addr, tickets) => {
    const rec = getOrCreate(index);
    const cost = BigInt(tickets) * ticketPrice;
    const cut = cutOf(cost);
    rec.cut += cut; st.heldCuts += cut;
    rec.pot += cost - cut;
    append(rec, addr, BigInt(tickets) * unitsPerTicket);
    rec.spendOf.set(addr, (rec.spendOf.get(addr) ?? 0n) + cost);
  };
  // The duel's rake arriving with the players it came from — raffle only.
  // FundPotWithEntries: the money and the entrants arrive together, and the
  // entries are EARNED, so they carry weight and no spendOf record.
  const seedRake = (index, addr, amount) => {
    const rec = getOrCreate(index);
    rec.pot += amount; rec.sponsored += amount;
    const units = amount / entryUnitPrice;
    if (units > 0n) append(rec, addr, units, true);
  };

  const settle = (index) => {
    const rec = get(index);
    rec.committed = true; rec.frozenTotal = totalOf(rec); rec.committedAt = st.height;
    const rnd = st.rounds.get(rec.round) ?? randHex();
    st.rounds.set(rec.round, rnd);
    rec.randomness = rnd;
    rec.resolved = true;
    rec.winner = ownerOfUnit(rec, randomnessMod(rnd, rec.frozenTotal));
    st.opsPot += rec.cut; st.heldCuts -= rec.cut; rec.cut = 0n;
  };

  // Three finished draws so "recent results" is never an empty promise, and one
  // sale already in the live draw so the pot is not zero at first glance.
  const c0 = current();
  for (let k = 3n; k >= 1n; k--) {
    const i = c0 - k;
    if (i < 0n) continue;
    seedBuy(i, OTHERS[Number(k) % OTHERS.length], k === 2n ? 3 : 1);
    seedBuy(i, OTHERS[(Number(k) + 1) % OTHERS.length], 2);
    if (raffle) seedRake(i, OTHERS[Number(k) % OTHERS.length], 3_600_000n * k);
    st.rounds.set(get(i).round, randHex());
    settle(i);
  }
  seedBuy(c0, OTHERS[0], raffle ? 2 : 4);
  seedBuy(c0, OTHERS[1], 1);
  if (raffle) { seedRake(c0, OTHERS[2], 7_200_000n); seedRake(c0, ME, 1_800_000n); }

  const tick = () => {
    st.height += 2n;
    st.latest += 1n;
    st.rounds.set(st.latest, randHex());
    getOrCreate(current()); // the live draw's record exists and pins its round
  };
  const timer = setInterval(tick, cadenceMs);

  const need = (cond, msg) => { if (!cond) throw new Error(`${game}: ${msg}`); };

  return {
    kind: 'rehearsal', game, me: ME, cadenceMs,
    stop() { clearInterval(timer); },
    // The rehearsal's own timer calls this every cadenceMs; it is exposed so a
    // test can advance the clock deterministically rather than by waiting.
    tick,

    // What the live client gets from economics() and CONFIG.
    economics: () => ({
      ticketPrice, entryUnitPrice, periodBlocks,
      entriesPerTicket: unitsPerTicket,
      source: 'rehearsal', periodSource: 'rehearsal', error: null,
    }),
    // The rehearsal's real block time: it advances two blocks every cadenceMs,
    // so this is what its countdown must be computed at — not any live chain's
    // rate, which would drift against the rehearsal's own height.
    blockSeconds: () => cadenceMs / 2000,

    async chainStatus() { return { height: Number(st.height), chainId: 'rehearsal' }; },
    async beaconLatest() { return { round: st.latest, randomness: st.rounds.get(st.latest) }; },
    async beaconPublished(round) { return st.rounds.has(BigInt(round)); },
    async wugnotBalance() { return st.balance; },
    async wugnotAllowance() { return st.allowance; },
    async approve(amount) { st.allowance = BigInt(amount); return { height: st.height }; },

    // --- reads, the realms' exported surface ---
    async currentIndex() { return current(); },

    // WeekEndHeight / WindowEndHeight, and their Current* one-call forms. The
    // arithmetic is the realms' own — launchHeight + (index+1)*periodBlocks —
    // INCLUDING the fencepost: the answer is the FIRST height of index+1, not
    // the last height of index, so at exactly this height currentIndex() has
    // already moved on (current() floors (height - launchHeight)/periodBlocks
    // the same way both realms do) and blocksUntil reads 0 rather than 1.
    async endHeight(i) { return st.launchHeight + (BigInt(i) + 1n) * periodBlocks; },
    async currentEndHeight() { return st.launchHeight + (current() + 1n) * periodBlocks; },
    async potSize() { return get(current())?.pot ?? 0n; },
    async ticketTotal() { const r = get(current()); return r ? totalOf(r) : 0n; },
    async ticketsOf(addr) { return get(current())?.ticketsOf.get(addr) ?? 0n; },
    async ticketTotalOf(i) { const r = get(i); return r ? totalOf(r) : 0n; },
    async ticketsOfIndex(i, addr) { return get(i)?.ticketsOf.get(addr) ?? 0n; },
    async spendOfIndex(i, addr) { return get(i)?.spendOf.get(addr) ?? 0n; },
    async escrowedCut(i) { return get(i)?.cut ?? 0n; },
    async capturedRandomness(i) { return get(i)?.randomness ?? ''; },
    async refundGraceBlocks() { return graceBlocks; },
    async refundOpensAt(i) {
      const r = get(i);
      if (!r || !r.committed || r.resolved || r.abandoned || r.committedAt <= 0n) return 0n;
      return r.committedAt + graceBlocks;
    },
    async drawOf(i) {
      const r = get(i);
      const t = r ?? blank(i);
      const tuple = raffle
        ? [t.committed, t.frozenTotal, r ? t.round : 0n, t.pot, t.resolved, t.winner, t.abandoned, t.sponsored, t.committedAt]
        : [t.committed, t.frozenTotal, r ? t.round : 0n, t.pot, t.resolved, t.winner, t.abandoned, t.committedAt];
      return normaliseDraw(tuple, raffle ? 'raffle' : 'speed');
    },
    async count() { return st.draws.size; },
    async indexes(offset, limit) {
      const w = drawWindow(offset, limit);
      const all = [...st.draws.values()].map((r) => r.index).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
      return all.slice(w.offset, w.offset + w.limit);
    },
    async opsPot() { return st.opsPot; },
    async heldCuts() { return st.heldCuts; },
    async auditOk() { return true; },
    async auditFailure() { return ''; },
    async windowBlocks() { return periodBlocks; },
    async maxStaleBlocks() { return periodBlocks * 2n; },
    async sponsored() { return get(current())?.sponsored ?? 0n; },
    async sponsoredOf(i) { return get(i)?.sponsored ?? 0n; },
    async claimPerUnitOf(i) { return get(i)?.claimPerUnit ?? 0n; },
    async unclaimedOf(i) { return get(i)?.claims ?? 0n; },
    async earnedOfIndex(i, addr) { return get(i)?.earnedOf.get(addr) ?? 0n; },
    async earnedTotalOf(i) { const r = get(i); return r ? [...r.earnedOf.values()].reduce((s, v) => s + v, 0n) : 0n; },
    async claimableOf(i, addr) {
      const r = get(i);
      if (!r || r.claimPerUnit <= 0n || r.claimed.has(addr)) return 0n;
      return (r.earnedOf.get(addr) ?? 0n) * r.claimPerUnit;
    },

    // --- actions, mirroring each realm's checks in order ---
    async buy(tickets) {
      const n = BigInt(tickets);
      need(n > 0n, 'ticket count must be positive');
      const cost = n * ticketPrice;
      const i = current();
      need(!(get(i)?.committed), 'this draw is already committed — tickets for it are closed');
      need(st.allowance >= cost, 'insufficient allowance — approve this realm for the ticket cost first');
      need(st.balance >= cost, 'wugnot: insufficient balance');
      st.allowance -= cost; st.balance -= cost;
      const rec = getOrCreate(i);
      const cut = cutOf(cost);
      rec.cut += cut; st.heldCuts += cut;
      rec.pot += cost - cut;
      append(rec, ME, raffle ? cost / entryUnitPrice : n);
      rec.spendOf.set(ME, (rec.spendOf.get(ME) ?? 0n) + cost);
      return { index: i, tickets: n, cost, height: st.height };
    },
    async commit(i) {
      need(BigInt(i) < current(), `a ${raffle ? 'week' : 'window'} can only be committed once it is over`);
      const rec = get(i);
      need(rec, 'no tickets sold — there is no rollover, so a drawless draw must not exist');
      need(!rec.committed, 'this draw is already committed');
      const total = totalOf(rec);
      need(total > 0n, 'no tickets sold — there is no rollover, so a drawless draw must not exist');
      rec.committed = true; rec.frozenTotal = total; rec.committedAt = st.height;
      return { round: rec.round, frozenTotal: total, height: st.height };
    },
    async resolve(i) {
      const rec = get(i);
      need(rec, 'no draw for that index');
      need(rec.committed, 'this draw is not committed yet');
      need(!rec.resolved, 'this draw is already resolved');
      need(!rec.abandoned, 'this draw was abandoned and refunded; it can never be resolved');
      need(st.rounds.has(rec.round), 'beacon round not published yet');
      rec.randomness = st.rounds.get(rec.round);
      rec.resolved = true;
      rec.winner = ownerOfUnit(rec, randomnessMod(rec.randomness, rec.frozenTotal));
      st.opsPot += rec.cut; st.heldCuts -= rec.cut; rec.cut = 0n;
      if (rec.winner === ME) st.balance += rec.pot;
      return { winner: rec.winner, pot: rec.pot, height: st.height };
    },
    async refund(i) {
      const rec = get(i);
      need(rec, 'no draw for that index');
      need(rec.committed, 'this draw is not committed yet');
      need(!rec.resolved, 'this draw is already resolved');
      need(st.height >= rec.committedAt + graceBlocks, 'the refund window has not opened yet');
      const owed = rec.spendOf.get(ME) ?? 0n;
      need(owed > 0n, 'you have nothing to refund here');
      if (!rec.abandoned && raffle && rec.sponsored > 0n) {
        // retireSponsorship: the house money becomes a per-entry claim on the
        // SAME draw, divided over the EARNED entries only.
        const earnedTotal = [...rec.earnedOf.values()].reduce((s, v) => s + v, 0n);
        if (earnedTotal > 0n) { rec.claimPerUnit = rec.sponsored / earnedTotal; rec.claims = rec.claimPerUnit * earnedTotal; }
        else st.opsPot += rec.sponsored;
        rec.sponsored = 0n;
      }
      rec.abandoned = true;
      rec.spendOf.set(ME, 0n);
      const share = rec.cut > 0n ? (rec.cut * owed) / (owed + [...rec.spendOf.values()].reduce((s, v) => s + v, 0n) || 1n) : 0n;
      rec.cut -= share; st.heldCuts -= share;
      rec.pot -= owed - share;
      st.balance += owed;
      return { amount: owed, height: st.height };
    },
    async claim(i) {
      const rec = get(i);
      need(rec, 'no draw for that index');
      need(rec.claimPerUnit > 0n, 'there is nothing to claim here');
      need(!rec.claimed.has(ME), 'you have already claimed this one');
      const amount = (rec.earnedOf.get(ME) ?? 0n) * rec.claimPerUnit;
      need(amount > 0n, 'your entries here were bought, not earned — a refund is your way out');
      rec.claimed.add(ME); rec.claims -= amount; rec.pot -= amount;
      st.balance += amount;
      return { amount, height: st.height };
    },
  };
}
